import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import EditIcon from '@material-ui/icons/Edit';
import FormEditor from './FormEditor';
import './LongTextFormEditor.scss';

const TextDialog = ({ text: propText, onClose, readOnly, validate, open, title }) => {
  const [{ text, error }, setText] = useState({});

  useEffect(() => {
    setText({ text: propText });
  }, [propText]);

  const handleCancel = () => {
    onClose();
  };

  const handleOk = () => {
    onClose(text);
  };

  const handleChange = (e) => {
    setText({
      text: e.target.value,
      error: validate && validate(e.target.value),
    });
  };

  return (
    <Dialog
      disableBackdropClick
      aria-labelledby="grb-edit-longtext-dialog-title"
      className={classnames('grb-form-longtext-dialog', 'grb-form-dialog')}
      onClose={handleCancel}
      open={open}
    >
      <DialogTitle id="grb-edit-longtext-dialog-title">{title}</DialogTitle>
      <DialogContent className="grb-form-longtext-dialog-content grb-form-dialog-content">
        <textarea
          className="grb-form-longtext-dialog-textarea"
          value={text || ''}
          readOnly={readOnly}
          onChange={handleChange}
        />
      </DialogContent>
      <DialogActions className="grb-form-longtext-dialog-buttons">
        <Button
          onClick={handleCancel}
          color="primary"
          id="cancel-edit-longtext"
        >
          Cancel
        </Button>
        <Button
          onClick={handleOk}
          color="primary"
          variant="contained"
          id="apply-edit-longtext"
          disabled={!!error || (text === propText)}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TextDialog.propTypes = {
  text: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  validate: PropTypes.func,
  title: PropTypes.string.isRequired,
};

TextDialog.defaultProps = {
  readOnly: false,
  validate: undefined,
};

const Component = ({ value, onChange, editor, errors, disabled, readOnly, validate }) => {
  const { id, name, label, property } = editor;
  const {
    description,
    dialogTitle,
    minWidth,
    maxWidth,
    linesCount = 1,
  } = property;
  const { dialog = linesCount < 2 } = property;

  const [open, setOpen] = useState(false);

  const handleClose = (text) => {
    setOpen(false);
    if (text !== undefined) {
      onChange(name, text);
    }
  };

  const handleEdit = () => {
    setOpen(true);
  };

  const error = errors && errors.length && errors[0];
  let style;
  if (minWidth || maxWidth) {
    style = {
      minWidth: minWidth ? `${minWidth}px` : undefined,
      maxWidth: maxWidth ? `${maxWidth}px` : undefined,
    };
  }

  const handleChange = (e) => {
    onChange(name, e.target.value);
  };

  return (
    <div
      key={id}
      id={id}
      className={classnames(
        'grb-form-editor-longtext',
        'grb-form-editor',
        dialog ? 'grb-form-editor-longtext-dialog' : 'grb-form-editor-longtext-inline',
        readOnly && 'grb-readonly',
      )}
      style={style}
    >
      <Typography className="grb-form-editor-longtext-label">{label}</Typography>
      {dialog ? (
        <React.Fragment>
          <FormControl
            className="grb-form-editor-longtext-text-control"
            error={!!error}
          >
            <Typography className="grb-form-editor-longtext-text" noWrap>{value || ''}</Typography>
            {(error || description) && (
              <FormHelperText>{error || description}</FormHelperText>
            )}
          </FormControl>
          <IconButton
            id={`${name}-open-dialog`}
            onClick={handleEdit}
            className="grb-form-editor-longtext-button"
            disabled={disabled}
          >
            <EditIcon />
          </IconButton>
          {open && (
            <TextDialog
              text={value || ''}
              onClose={handleClose}
              validate={validate}
              readOnly={readOnly}
              open
              title={dialogTitle}
            />
          )}
        </React.Fragment>
      ) : (
        <FormControl
          className="grb-form-editor-longtext-text-control"
          error={!!error}
        >
          <textarea
            className="grb-form-longtext-textarea"
            value={value || ''}
            readOnly={readOnly}
            rows={linesCount || 4}
            onChange={handleChange}
          />
          {(error || description) && (
            <FormHelperText>{error || description}</FormHelperText>
          )}
        </FormControl>
      )}
      {/* <TextField
        className={classnames(
          'grb-form-editor-longtext-field',
          dense && 'grb-form-editor-dense',
          'grb-readonly',
        )}
        error={!!error}
        label={label}
        value={value && getShortText(value) || ''}
        margin={margin || (dense && 'dense') || undefined}
        InputProps={InputProps}
        fullWidth={fullWidth}
        helperText={error || description || ''}
        disabled={disabled}
        required={required}
        {...userProps}
      /> */}
    </div>
  );
};

Component.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  editor: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.string,
    property: PropTypes.shape({
      type: PropTypes.string,
      dense: PropTypes.bool,
      description: PropTypes.string,
      multiline: PropTypes.bool,
      min: PropTypes.number,
      max: PropTypes.number,
      autoComplete: PropTypes.string,
      startAdornment: PropTypes.string,
      endAdornment: PropTypes.string,
      margin: PropTypes.string,
      fullWidth: PropTypes.bool,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      required: PropTypes.bool,
      dialog: PropTypes.bool,
      dialogTitle: PropTypes.string,
      linesCount: PropTypes.number,
    }),
    Component: PropTypes.func,
    componentProps: PropTypes.func,
  }).isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  data: PropTypes.shape({}).isRequired,
  validate: PropTypes.func,
};

Component.defaultProps = {
  value: undefined,
  errors: undefined,
  disabled: undefined,
  readOnly: false,
  validate: undefined,
};

class LongTextFormEditor extends FormEditor {
  constructor(property, options) {
    super(property, options);
    this.Component = Component;
    this.componentProps = options && options.props || (() => {});
  }
}

export default LongTextFormEditor;
